import {
  ReportSheetCategoriesWithTotal,
  ReportSheetGroupsWithStats,
  ReportSheetPrimariesWithStats,
  Total,
} from '../types';

export interface BalanceFrs {
  capitalAndReserves: {
    calledUpSharedCapital: number;
    retainedEarnings: number;
  };
  creditors: {
    amountDueInOneYear: number;
    customerLiabilities: number;
    total: number;
  };
  currentAssets: {
    cash: number;
    debtors: number;
    investments: number;
    total: number;
  };
  fixedAssets: {
    financialExpenses: number;
    intangibleAssets: number;
    propertyPlantEquipment: number;
    total: number;
  };
  netCurrentAssets: {
    total: number;
  };
  shareholderFunds: {
    total: number;
  };
  totalAssetsLessLiabilities: {
    total: number;
  };
}

export interface AccountsStats {
  count: number;
  id: string;
  accountGroups: {
    count: number;
    id: string;
    primaryAccounts: {
      count: number;
      id: string;
    }[];
  }[];
}

export interface Category {
  active: boolean;
  id: string;
  isClientAccount: boolean;
  name: string;
  number: string;
  accountGroups?: Group[];
  type: string;
}

export interface Group {
  id: string;
  description: string;
  number: number;
  active?: boolean;
  categoryId?: string;
  category?: Category;
  isClientAccount?: boolean;
  primaryAccounts?: PrimaryAccount[];
}

export interface PrimaryAccount {
  description: string;
  number: string;
  active: boolean;
  groupId: string;
  group: Group;
  id: string;
}

export enum AccountType {
  DEBIT = 'Debit',
  CREDIT = 'Credit',
}

export interface AccountReport {
  id: string;
  number: string;
  description: string;
  type: string;
  active: boolean;
  render?: Record<string, React.JSX.Element>;
}

export interface BalanceSheet {
  categories: Record<string, ReportSheetCategoriesWithTotal>;
  groups: Record<string, ReportSheetGroupsWithStats>;
  primaries: Record<string, ReportSheetPrimariesWithStats>;
  balanceSheetFRS: BalanceFrs;
  totalAssets?: Total;
  totalLiabilities?: Total;
  checksum?: number;
}

export interface BalanceSheetAccounts {
  accounts: Record<string, BalanceSheetAccountsWithStats>;
}

export interface AccountsReport {
  id: string;
  accountId?: string;
  categoryId?: string;
  creditIn?: number;
  creditOut?: number;
  creditTo?: number;
  debitIn?: number;
  debitOut?: number;
  debitTo?: number;
  groupId?: number;
  primaryId?: number;
  accountType?: string;
  closed?: boolean;
  fullNumber?: string;
  search?: string;
  url?: string;
  render?: Record<string, React.JSX.Element>;
}

export interface AccountsReportResponse {
  items: AccountsReport[];
}

export interface BalanceSheetAccountsWithStats {
  id: string;
  active: boolean;
  description: string;
  number: string;
  closed: boolean;
  debitBlock: boolean;
  creditBlock: boolean;
  iban?: string;
  fullNumber: string;
  clientName: string;
  categoryId: string;
  groupId: string;
  primaryId: string;
  userId?: string;
  organizationId?: string;
  balance: number;
  closedAt?: string;
  accountType: AccountType;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
  debitIn?: number;
  debitOut?: number;
  debitTo?: number;
  creditIn?: number;
  creditOut?: number;
  creditTo?: number;
  stats: Total;
}

export interface AccountReportItem {
  id: string;
  active: boolean;
  description: string;
  number: string;
  closed: boolean;
  debitBlock: boolean;
  creditBlock: boolean;
  iban?: string;
  fullNumber: string;
  clientName: string;
  categoryId: string;
  groupId: string;
  primaryId: string;
  userId?: string;
  organizationId?: string;
  balance: number;
  closedAt?: string;
  accountType: AccountType;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
  debitIn: number;
  debitOut: number;
  debitTo: number;
  creditIn: number;
  creditOut: number;
  creditTo: number;
  accountActive: boolean;
  accountId: string;
  url?: string;
  render?: Record<string, React.JSX.Element>;
}
